import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { IImpersonationContextComponentProps } from '../../../../../models';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getRichContentMessageForTextingStep } from '../../../../../models/UiUtils';
import { useUpdateAutomationStepMutation } from '../../../../../queries/Automation/mutations/steps';
import { IConnectionTypeComponentProps } from '../../../../containers/templates/EditCampaign/context';
import { bs } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { Collapsible } from '../../../Collapsible';
import { InputFieldError } from '../../../InputFieldError';
import { TextInput } from '../../../TextInput';
import { AutomationTagAutocomplete } from '../../AutomationTriggerCard/presentation';
import { EditAutomationStepEmailComposerModal } from '../../EditAutomationStepEmailComposerModal';
import { AdvancedOptionsButton } from '../presentation';
import { styleSheet } from './../styles';
import { ContactFilterOption } from './ContactFilterOption';
import { StepSendFromOption } from './StepSendFromOption';

interface EmailStepConfigProps extends IImpersonationContextComponentProps, IConnectionTypeComponentProps {
	step: Api.IAutomationStep;
	canEdit: boolean;
	templateId: string;
	errorMessage: string | null;
	trigger: Api.IAutomationTrigger;
	schedule: Api.IAutomationStepSchedule;
	onClearErrorMessages: () => void;
	onStepChanged?: () => void;
	updateStep?: (stepId: string, updater: (step: Api.IAutomationStep) => Api.IAutomationStep) => void;
}

export function EmailStepConfig({
	step,
	canEdit,
	templateId,
	errorMessage,
	trigger,
	schedule,
	onClearErrorMessages,
	onStepChanged,
	impersonationContext,
	connectionTypes,
	updateStep,
}: EmailStepConfigProps) {
	const userSession = useUserSession();
	const emailStep = step as Api.IEmailAutomationStep;

	const [name, setName] = React.useState(step?._type ? step.name : null);
	const [tagToAddOnClick, setTagToAddOnClick] = React.useState(emailStep?.tagToAddOnClick ?? null);
	const [showEmailLinkClickTags, setShowEmailLinkClickTags] = React.useState(!!emailStep?.tagToAddOnClick);
	const [skipStepContactFilter, setSkipStepContactFilter] = React.useState(step?.contactFilter);
	const [showEmailEditor, setShowEmailEditor] = React.useState(false);
	const [advancedOptionsToggled, setAdvancedOptionsToggled] = React.useState(() => {
		if (!!emailStep?.tagToAddOnClick || !!emailStep?.options?.sendToSender) {
			return true;
		}

		const hasSendOptionSelected =
			!!step?.sendFromOptions && step.sendFromOptions.mode !== Api.SendEmailFrom.CurrentUser;
		return skipStepContactFilter?.criteria || hasSendOptionSelected;
	});

	const updateStepMutation = useUpdateAutomationStepMutation({
		onSuccess: () => onStepChanged?.(),
	});

	const save = ({
		newTagToAddOnClick = tagToAddOnClick,
		newContactFilter = skipStepContactFilter,
		newSendFrom,
		newSendToSender,
	}: {
		newTagToAddOnClick?: string;
		newContactFilter?: Api.IContactFilterCriteria;
		newSendFrom?: Api.ISendFromOptions | null;
		newSendToSender?: boolean | null;
	} = {}) => {
		const stepModel: Partial<Api.IEmailAutomationStep> = {
			...((step || {}) as Api.IEmailAutomationStep),
			name,
			tagToAddOnClick: newTagToAddOnClick,
			contactFilter: newContactFilter,
		};

		if (newSendToSender !== undefined) {
			stepModel.options = stepModel.options || { noteVisibility: 'all', saveAsNote: true };
			stepModel.options.sendToSender = !!newSendToSender;
		}

		if (newSendFrom !== undefined) {
			stepModel.sendFromOptions = newSendFrom;
		}

		updateStepMutation.mutate({
			templateId,
			stepId: step.id,
			stepType: Api.AutomationStepType.Email,
			step: stepModel,
			impersonationContext,
		});
	};

	const onNameInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setName(e.target.value);
		onClearErrorMessages();
	};

	const onEmailCtaClicked = () => {
		setShowEmailEditor(true);
		onClearErrorMessages();
	};

	const onTagForLinkClickChanged = (tag: string) => {
		setTagToAddOnClick(tag);
		save({ newTagToAddOnClick: tag });
	};

	const onAddTagToLinkClickCheckChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setShowEmailLinkClickTags(checked);
		if (!checked) {
			onTagForLinkClickChanged('');
		}
	};

	const onEmailComposerModalRequestClose = (_data: Api.IEmailAutomationStep) => {
		setShowEmailEditor(false);
	};

	const saveSenderOptions = (option: Api.ISendFromOptions, sendToSender: boolean) => {
		save({ newSendFrom: option, newSendToSender: sendToSender });
	};

	const baseKey = `${step?._type}-${step?.id || ''}`;

	return (
		<>
			<div className={css(styleSheet.fieldLabel)}>
				Email title: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<TextInput
				className={css(styleSheet.emailNameInput)}
				disabled={!canEdit}
				inputId={`automation-email-step-name-input-${step.id || 'unsaved'}`}
				key={`${baseKey}-name-input`}
				onBlur={() => save()}
				onChange={onNameInputChanged}
				onFocus={onClearErrorMessages}
				placeholder='Enter a brief description of this email'
				type='text'
				value={name || ''}
			/>
			<InputFieldError errorMessage={errorMessage}>
				<button
					className={css(
						styleSheet.emailCta,
						!emailStep.content ? styleSheet.emailCreateButton : styleSheet.emailEditButton
					)}
					disabled={!step}
					onClick={onEmailCtaClicked}
				>
					<span>{`${emailStep.content ? 'Preview / Edit' : 'Create'} Email`}</span>
				</button>
			</InputFieldError>
			<AdvancedOptionsButton
				isCollapsed={!advancedOptionsToggled}
				onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
			/>
			<Collapsible isCollapsed={!advancedOptionsToggled}>
				<div className={css(styleSheet.advancedOptionsContent)}>
					<Checkbox
						className={css(styleSheet.addTagOnLinkClickCheckbox)}
						id={step.id ? `automation-add-tag-on-link-click-checkbox-${step.id}` : uuid()}
						checked={showEmailLinkClickTags}
						type='large'
						onChange={onAddTagToLinkClickCheckChanged}
					>
						<div>
							<div className={css(styleSheet.checkboxText)}>
								Add a tag if the recipient clicks (any) link in the email
							</div>
						</div>
					</Checkbox>
					{showEmailLinkClickTags ? (
						<AutomationTagAutocomplete
							className={css(styleSheet.tagAutomcomplete)}
							impersonationContext={impersonationContext}
							limit={5}
							onTagSelected={onTagForLinkClickChanged}
							initialTag={tagToAddOnClick}
						/>
					) : null}
					<StepSendFromOption
						showInternalSendFromOption
						step={step}
						onSave={saveSenderOptions}
						connectionTypes={connectionTypes}
						impersonationContext={impersonationContext}
					/>
					<ContactFilterOption
						canEdit={canEdit}
						impersonationContext={impersonationContext}
						skipStepContactFilter={skipStepContactFilter}
						stepUuid={step.id}
						stepIsBusy={false}
						onFilterChange={filter => {
							setSkipStepContactFilter(filter);
							save({ newContactFilter: filter });
						}}
					/>
				</div>
			</Collapsible>
			{showEmailEditor ? (
				<EditAutomationStepEmailComposerModal
					automationTemplateId={templateId}
					step={step as Api.IEmailAutomationStep}
					impersonationContext={impersonationContext}
					onRequestClose={onEmailComposerModalRequestClose}
					onStepChanged={onStepChanged}
					onAttachmentRemoved={data => {
						updateStep?.(step.id, () => data as Api.IAutomationStep);
					}}
					readonly={!canEdit}
					trigger={trigger}
					defaultInitialContent={Api.createRawRichTextContentStateWithText(
						getRichContentMessageForTextingStep(userSession, trigger, schedule)
					)}
				/>
			) : null}
		</>
	);
}
